import { styled } from '@mui/system'
import { colorPalette } from '../../style/colorPalette'
import { intl } from '../../../i18n'
import {
  LoginButton,
  LoginContainer,
  LoginPassword,
  LoginTitle,
} from './components'
import { PasswordVisibilityAdornment } from '../../components/inputs/PasswordVisibilityAdornment'
import { NoticeText } from '../../components/labels/NoticeText'
import { useHistory, useRouteMatch } from 'react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePasswordField } from '../../hooks/usePasswordField'
import { useAuthenticateUseCase } from '../../../applications/usecases/authentication/authenticationUseCases'
import { AuthError } from '../../../error/AuthError'
import { ErrorMessage } from './components/AuthErrorMessage'
import Auth from '../../../lib/commons/auth'
import { whitespaceNoticeViewOf } from '../../../utils/password'

const EmailArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  height: '72px',
  padding: '0 16px',
  gap: '4px',
  borderRadius: '4px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[1],
  width: '444px',
})

const EmailTitle = styled('div')({
  display: 'flex',
  color: colorPalette.monotone[4],
  fontSize: '14px',
  fontWeight: '400',
})

const Email = styled('div')({
  display: 'flex',
  fontSize: '14px',
  color: colorPalette.monotone[10],
})

export const ChangePasswordForNewUser = () => {
  const { completeNewPassword } = useAuthenticateUseCase()
  const match = useRouteMatch<{ email: string }>()
  const email = useMemo(() => match.params.email, [match.params.email])
  const newPassword = usePasswordField()
  const newPasswordConfirm = usePasswordField()
  const [authError, setAuthError] = useState<AuthError | undefined>(undefined)
  const history = useHistory()

  useEffect(() => {
    const caller = async () => {
      const tenant = Auth.getCurrentTenant()
      if (tenant) {
        const isLoggedIn = await tenant.isLoggedin()
        if (isLoggedIn) {
          history.push('/')
        }
      }
      if (!Auth.hasTemporaryPassword()) {
        history.push('/')
      }
    }
    caller()
  }, [history])
  const onSubmit = useCallback(async () => {
    if (!newPassword.value || !newPasswordConfirm.value) {
      setAuthError(new AuthError('NO_PASSWORD_INPUT'))
      return
    }
    if (newPassword.value !== newPasswordConfirm.value) {
      setAuthError(new AuthError('PASSWORD_CONFIRM_UNMATCH'))
      return
    }
    completeNewPassword(email, newPassword.value).catch(err => {
      if (!(err instanceof AuthError)) {
        throw err
      }
      setAuthError(err)
    })
  }, [newPassword.value, newPasswordConfirm.value, completeNewPassword, email])
  const toLogin = useCallback(() => {
    history.push('/')
  }, [history])

  return (
    <LoginContainer>
      <LoginTitle>
        {intl.formatMessage({ id: 'changePasswordNewUser.setNewPassword' })}
      </LoginTitle>
      {authError && <ErrorMessage authError={authError} />}
      <EmailArea>
        <EmailTitle>
          {intl.formatMessage({ id: 'changePasswordNewUser.username' })}
        </EmailTitle>
        <Email>{email}</Email>
      </EmailArea>

      <LoginPassword
        label={intl.formatMessage({ id: 'changePassword.newPassword' })}
        type={newPassword.inputType}
        autoComplete="new-password"
        value={newPassword.value}
        onChange={newPassword.onChange}
        onKeyDown={newPassword.onKeyDown}
        InputProps={{
          endAdornment: (
            <PasswordVisibilityAdornment
              visible={newPassword.showPassword}
              onToggle={newPassword.toggleShowPassword}
            />
          ),
        }}
        helperText={
          <NoticeText
            {...whitespaceNoticeViewOf(newPassword.whitespaceNotice)}
          />
        }
      />
      <LoginPassword
        label={intl.formatMessage({
          id: 'changePassword.newPasswordForConfirm',
        })}
        type={newPasswordConfirm.inputType}
        autoComplete="off"
        value={newPasswordConfirm.value}
        onChange={newPasswordConfirm.onChange}
        onKeyDown={newPasswordConfirm.onKeyDown}
        InputProps={{
          endAdornment: (
            <PasswordVisibilityAdornment
              visible={newPasswordConfirm.showPassword}
              onToggle={newPasswordConfirm.toggleShowPassword}
            />
          ),
        }}
        helperText={
          <NoticeText
            {...whitespaceNoticeViewOf(newPasswordConfirm.whitespaceNotice)}
          />
        }
      />
      <LoginButton color={'skyBlue'} onClick={onSubmit}>
        {intl.formatMessage({ id: 'forgotPasswordSubmit.setPassword' })}
      </LoginButton>
      <LoginButton color={'monotone'} onClick={toLogin}>
        {intl.formatMessage({ id: 'back' })}
      </LoginButton>
    </LoginContainer>
  )
}
